/**
 * Ce que le tunnel écrit au sujet d'une demande : les bornes, le refus motivé,
 * et le corps envoyé. Pur, sans I/O ni React — donc couvrable exhaustivement.
 *
 * Porté depuis `src/lib/booking-requests.ts` du repo rj-atelier, qui portait le
 * tunnel avant FIXERLIGHT-020. Les règles sont celles de l'API et n'ont pas
 * changé ; seule la langue du fichier a changé.
 */

/**
 * Le plafond de créneaux proposables, imposé par l'API. Le tunnel l'applique
 * AVANT la saisie du onzième plutôt que de le découvrir en erreur : une
 * contrainte s'exprime avant la saisie, pas après.
 */
export const MAX_PROPOSED_SLOTS = 10;

/** L'étendue maximale entre le premier et le dernier créneau proposé, en jours. */
export const MAX_SPAN_DAYS = 21;

/** Le nombre de jours couverts par une sélection, bornes comprises. */
function spanDays(slots) {
  if (slots.length === 0) return 0;
  const times = slots.map((iso) => new Date(iso).getTime());
  return (Math.max(...times) - Math.min(...times)) / (24 * 60 * 60 * 1000);
}

/**
 * Pourquoi ce créneau ne peut pas rejoindre la sélection, en toutes lettres, ou
 * `null` s'il le peut. Retirer un créneau déjà retenu est toujours possible.
 */
export function slotRefusal(slots, iso) {
  if (slots.includes(iso)) return null;
  if (slots.length >= MAX_PROPOSED_SLOTS) {
    return `Vous pouvez proposer ${MAX_PROPOSED_SLOTS} créneaux au maximum.`;
  }
  if (spanDays([...slots, iso]) > MAX_SPAN_DAYS) {
    return `Vos créneaux doivent tenir sur ${MAX_SPAN_DAYS} jours.`;
  }
  return null;
}

/**
 * Le corps de la demande : les créneaux retenus, triés, et rien d'autre.
 *
 * Un seul créneau part sous la forme historique `scheduled_at`, que l'API
 * accepte toujours. C'est ce qui rend son kill-switch multi-créneaux réellement
 * rétractable : baissé, il refuse `proposed_slots` en 422, et un tunnel qui
 * n'enverrait que cette forme tomberait en entier au lieu de retomber sur la
 * réservation à un créneau.
 *
 * Aucun `client_id` ici, et ce n'est pas un oubli : il vient du jeton résolu
 * côté api par `ResolveRjClient`, et l'API refuse qu'un corps le porte.
 */
export function buildBookingBody({ service, slots, autodiag, customer }) {
  const sorted = [...slots].sort((a, b) => a.localeCompare(b));

  if (sorted.length === 1) {
    return { service, scheduled_at: sorted[0], autodiag, customer };
  }

  return { service, proposed_slots: sorted, autodiag, customer };
}

/**
 * Les réponses du questionnaire → la charge utile `autodiag` attendue par
 * l'API, ou `null` si rien n'a été répondu.
 *
 * Les réponses vides sont écartées plutôt qu'envoyées à blanc : une clé sans
 * valeur n'apprend rien au réparateur et alourdit la fiche qu'il lit debout,
 * une main sur le vélo.
 */
export function toAutodiagPayload(questionnaire, answers) {
  if (!questionnaire) return null;

  const filled = (questionnaire.questions || [])
    .map(({ key }) => ({ key, value: answers[key] }))
    .filter(
      ({ value }) =>
        value !== undefined && value !== null && String(value).trim() !== '',
    );

  if (filled.length === 0) return null;

  return { version: questionnaire.version, answers: filled };
}

/**
 * Le bloc `customer` de la demande, depuis le COMPTE du client.
 *
 * Le compte est obligatoire (décision du 2026-09-21) : nom, téléphone et email
 * viennent de lui et ne se ressaisissent pas. Seule l'adresse d'intervention
 * vient du tunnel. `client_id`, lui, ne part jamais dans le corps : l'api le
 * tire du jeton (`BookingOrigin::annuaire`).
 *
 * `position` : `{ latitude, longitude, place_id }` de la suggestion choisie,
 * ou `null` pour une adresse tapée librement (FIXERLIGHT-073). Les trois
 * partent ensemble ou pas du tout, et jamais sans texte d'adresse : l'api
 * refuse un sous-ensemble (`AddressCoordinates`).
 */
export function customerDepuisCompte(client, adresse, position = null) {
  const nom = [client && client.prenom, client && client.nom]
    .filter(Boolean)
    .join(' ')
    .trim();
  const address = (adresse || '').trim() || null;
  return {
    name: nom,
    phone: (client && client.tel) || null,
    email: (client && client.email) || null,
    address,
    ...champsDePosition(address, position),
  };
}

/**
 * `{ latitude, longitude, place_id }` d'une adresse choisie parmi les
 * suggestions, ou `{}` : les trois ensemble ou aucun, et jamais sans texte
 * d'adresse (l'api refuse un sous-ensemble, `AddressCoordinates`).
 */
function champsDePosition(address, position) {
  const situee =
    address !== null &&
    position &&
    typeof position.latitude === 'number' &&
    typeof position.longitude === 'number' &&
    typeof position.place_id === 'string' &&
    position.place_id !== '';
  return situee
    ? {
        latitude: position.latitude,
        longitude: position.longitude,
        place_id: position.place_id,
      }
    : {};
}

/**
 * Le supplément de déplacement affiché au client, TTC (FIXERLIGHT-048/066).
 * Même convention de collage que `ShopPrestations::formatPrice` (ticket #35,
 * € collé au chiffre) : pas de décimale forcée sur un montant rond.
 */
export function formatSupplement(eur) {
  if (eur === null || eur === undefined) return null;
  return Number.isInteger(eur)
    ? `${eur}€`
    : `${eur.toFixed(2).replace('.', ',')}€`;
}

/**
 * Les créneaux que l'api vient de refuser, lus dans les clés d'une 422.
 *
 * L'api refuse TOUTE la demande dès qu'un créneau n'est plus proposable, et
 * nomme le fautif : `scheduled_at` pour une demande à un créneau,
 * `proposed_slots.{i}` sinon, où `i` est l'index dans le tableau TRIÉ envoyé
 * (`buildBookingBody` trie). Le tunnel retire ces créneaux de la sélection et
 * laisse le client repartir avec les autres.
 */
export function creneauxRefuses(fieldErrors, envoyes) {
  const tries = [...envoyes].sort((a, b) => a.localeCompare(b));
  return Object.keys(fieldErrors || {})
    .map((cle) => {
      if (cle === 'scheduled_at') return tries[0];
      const m = /^proposed_slots\.(\d+)$/.exec(cle);
      return m ? tries[Number(m[1])] : null;
    })
    .filter(Boolean);
}

/**
 * Le bloc `customer` de la demande, depuis les COORDONNÉES saisies dans le
 * tunnel de `/reserver/:slug` : pas de compte, donc rien ne vient du magasin.
 * L'api n'exige que l'email OU le téléphone ; le tunnel, lui, demande les
 * quatre champs (`coordonnees.js`). `position` : comme pour
 * `customerDepuisCompte`.
 */
export function customerDepuisCoordonnees(
  coordonnees,
  adresse,
  position = null,
) {
  const c = coordonnees || {};
  const address = (adresse || '').trim() || null;
  // Le prénom et le nom partent séparés (l'api compose le nom complet) : le
  // courriel que reçoit le client le salue par son prénom.
  return {
    first_name: (c.prenom || '').trim(),
    last_name: (c.nom || '').trim(),
    phone: (c.tel || '').trim() || null,
    email: (c.email || '').trim() || null,
    address,
    ...champsDePosition(address, position),
  };
}
